import styled from "styled-components";
import { useDispatch } from "react-redux";

import Button from "../Button/Button";
import { NotEnoughCredits } from "../../types/notEnoughCredits";
import { Popups, updatePopup } from "../../redux/actions/popupsActions";
import { BinIcon, FillLockIcon, LikeActiveIcon, LikeIcon } from "../Icons/Icons";
import { CustomAbsoluteBox } from "../../modules/AIHumans/components/tabs/tabs.styles";

interface Props {
  name?: string;
  photo?: string;
  active?: boolean;
  isBookmarked?: boolean;
  isDeleteButton?: boolean;
  handleActive?: () => void;
  handleBookmarked?: (e: any) => void;
  handlePlay: (e: any) => void;
  handleSelect: () => void;
  handleCustomHumatarDelete: (e: any) => void;
  isLocked: boolean;
}

const NewHumanCard = ({
  photo,
  name,
  active,
  isBookmarked,
  isDeleteButton,
  handleActive,
  handleBookmarked,
  handlePlay,
  handleSelect,
  handleCustomHumatarDelete,
  isLocked = false,
}: Props) => {
  const dispatch = useDispatch();
  const handleUpgradePopupOpen = () => {
    const prefilled: NotEnoughCredits = {
      title: "Upgrade the plan to use this humatar",
      description: "Get access to additional premium features and Humatars instantly",
    };

    dispatch(
      updatePopup({
        popup: Popups.notEnoughCreditsPopup,
        status: true,
        prefilled,
      }),
    );
  };

  return (
    <Wrapper active={active} onClick={isLocked ? handleUpgradePopupOpen : handleActive}>
      <Image src={photo} />
      <ActionButtonsWrapper className="action-buttons">
        <IconWrapper onClick={handleBookmarked} top={13} left={13}>
          {!isBookmarked ? (
            <LikeIcon color="#3100F7" width={24} height={24} />
          ) : (
            <LikeActiveIcon color="#3100F7" width={24} height={24} />
          )}
        </IconWrapper>

        {isDeleteButton && (
          <IconWrapper onClick={handleCustomHumatarDelete} top={13} right={13}>
            <BinIcon color="#ffffff" width={20} height={20} />
          </IconWrapper>
        )}
        <Button text="Select" onClick={handleSelect} />
        {/* <Button text="Play" onClick={handlePlay} /> */}
      </ActionButtonsWrapper>

      {!isLocked ? (
        <Bottom>
          <HumanInfo>
            <Name>{name}</Name>
          </HumanInfo>
        </Bottom>
      ) : (
        <LockWrapper>
          <Circle>
            <FillLockIcon />
          </Circle>
          <span>
            Unlock the humatar by <b>Upgrading the plan</b>
          </span>
        </LockWrapper>
      )}
      {isLocked && <Blur />}
    </Wrapper>
  );
};

const Wrapper = styled.div<{ active?: boolean }>`
  position: relative;
  display: flex;
  border-radius: 10px;
  max-width: 200px;
  height: 238px;
  background-color: #242425;
  overflow: hidden;
  cursor: pointer;

  &:hover > .delete-button > button {
    opacity: 1;
    transition: 0.2s;
  }

  & > .delete-button > button {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 24px;
    height: 24px;
    box-shadow: none;
    opacity: 0;

    svg {
      width: 12px;
      height: 12px;

      path {
        fill: #fff;
      }
    }
  }
  border: ${({ active, theme }) => (active ? `2px solid ${theme.activeMenu}` : "none")};
  .action-buttons {
    display: ${({ active }) => (active ? "flex" : "none")};
  }

  @media (max-width: 516px) {
    height: 136px;
    width: 100%;
  }
`;

const Image = styled.img`
  position: absolute;
  max-width: 200px;
  height: 200px;
  top: 0;
  left: 0;
  object-fit: cover;
`;

const ActionButtonsWrapper = styled.div`
  box-shadow: 0px 4px 30.1px 50px #1e37a66e inset;
  position: absolute;
  inset: 0 0 35px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  & button {
    width: 68px;
    height: 34px;
    border-radius: 20px;
    box-shadow: none;
    justify-content: center;
    :first-of-type {
      background-color: #0063f7b0;
    }
    :last-of-type {
      background: #7300f7b0;
    }
    & span {
      font-size: 10px;
      font-weight: 600;
      text-align: center;
    }
  }
`;

const IconWrapper = styled(CustomAbsoluteBox)<{ active?: boolean }>`
  cursor: pointer;
  z-index: 2;
  width: 30px;
  height: 30px;
  border-radius: 5px;
  background: #242425;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Bottom = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 38px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HumanInfo = styled.div`
  display: flex;
  column-gap: 8px;
  align-items: center;
`;

const Name = styled.p`
  font-family: "Mulish";
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.primaryText};
`;

const Actions = styled.div`
  display: flex;
  column-gap: 8px;
`;

const IconButtonWrapper = styled.div`
  button {
    width: 20px;
    height: 20px;

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const Blur = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 19px;
  pointer-events: none;
  background: rgba(25, 27, 31, 0.68);
`;

const LockWrapper = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 2;
  pointer-events: none;

  span {
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    color: ${({ theme }) => theme.white};

    b {
      font-weight: 700;
      color: ${({ theme }) => theme.activeMenu};
    }
  }
`;

const Circle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export default NewHumanCard;
